'use client';

import { useCallback, useMemo, useState } from 'react';

import type { Place, TypesenseEventDocument } from '@/types';
import { addMonths } from 'date-fns';
import {
  useClearRefinements,
  useCurrentRefinements,
  useInfiniteHits,
  useInstantSearch,
  useRange,
  useRefinementList,
} from 'react-instantsearch';

export const useEvents = (placesData: Place[] | undefined) => {
  const { items: events, showMore, isLastPage } = useInfiniteHits<TypesenseEventDocument>();
  const { items: filters } = useCurrentRefinements();
  const { status, setIndexUiState } = useInstantSearch();

  // place refinement
  const { items: places, refine: setPlace } = useRefinementList({
    attribute: '_referencedBy',
  });
  const { refine: clearPlace } = useClearRefinements({
    includedAttributes: ['_referencedBy'],
  });
  const place = useMemo(() => filters.find((f) => f.attribute === '_referencedBy')?.refinements?.[0]?.value, [filters]);

  // city refinement
  const [city, setCityFn] = useState<string | null>(null);

  // Memoize cities computation to avoid recalculating on every render
  const cities = useMemo(() => {
    return placesData
      ?.reduce(
        (acc, place) => {
          const city = place.default.address.cityOrTown;
          const currentCity = acc.find((c) => c.value === city);
          const eventsAtPlace = places.find((p) => p.value === place.id)?.count;

          if (city && !currentCity) {
            acc.push({ value: city, count: eventsAtPlace || 0 });
          } else if (currentCity) {
            currentCity.count += eventsAtPlace || 0;
          }

          return acc;
        },
        [] as { value: string; count: number }[],
      )
      .filter((c) => Boolean(c.count));
  }, [placesData, places]);

  const setCity = useCallback(
    (city: string) => {
      setCityFn(city);
      const filteredPlaces = placesData?.filter((p) => p.default.address.cityOrTown === city);

      if (filteredPlaces) {
        setIndexUiState((prev) => ({
          ...prev,
          refinementList: {
            ...prev.refinementList,
            _referencedBy: filteredPlaces.map((p) => p.id),
          },
        }));
      }
    },
    [placesData, setIndexUiState],
  );

  const clearCity = useCallback(() => {
    setCityFn(null);
    clearPlace();
  }, [clearPlace]);

  // Category refinement
  const { items: categories } = useRefinementList({
    attribute: 'category',
  });
  const setCategory = (value: string) => {
    setIndexUiState((prev) => ({
      ...prev,
      refinementList: {
        ...prev.refinementList,
        category: [value],
      },
    }));
  };
  const { refine: clearCategory } = useClearRefinements({
    includedAttributes: ['category'],
  });
  const category = useMemo(() => filters.find((f) => f.attribute === 'category')?.refinements?.[0]?.value, [filters]);

  // Date range refinement
  const { refine: setDateRange, range: dateRange } = useRange({
    attribute: 'dates',
  });
  const date = useMemo(
    () => filters.find((f) => f.attribute === 'dates')?.refinements?.map((r) => r.value) as [number, number],
    [filters],
  );
  const { items: availableDates } = useRefinementList({
    attribute: 'dates',
  });

  const minTimestamp = dateRange?.min || Date.now() / 1000;
  const maxTimestamp = dateRange?.max || Date.now() / 1000;
  const maxDate = useMemo(() => new Date(maxTimestamp * 1000), [maxTimestamp]);
  const minDate = useMemo(() => new Date(minTimestamp * 1000), [minTimestamp]);

  const fromTimestamp = date?.[0] || minTimestamp;
  const fromDate = useMemo(() => new Date(fromTimestamp * 1000), [fromTimestamp]);

  const toTimestamp = date?.[1] || addMonths(fromDate, 1).getTime() / 1000;
  const toDate = useMemo(() => new Date(toTimestamp * 1000), [toTimestamp]);

  const previousMonth = useCallback(
    () => setDateRange([Math.max(addMonths(fromDate, -1).getTime() / 1000, minTimestamp), fromTimestamp]),
    [setDateRange, fromDate, minTimestamp, fromTimestamp],
  );

  const nextMonth = useCallback(
    () => setDateRange([toTimestamp, Math.min(addMonths(toDate, 1).getTime() / 1000, maxTimestamp)]),
    [setDateRange, toTimestamp, toDate, maxTimestamp],
  );

  const setFromDate = useCallback(
    (date: Date) => setDateRange([Math.round(date.getTime() / 1000), maxTimestamp]),
    [setDateRange, maxTimestamp],
  );

  return {
    /** Start of the selected date range */
    fromDate,
    /** End of the selected date range */
    toDate,
    /** Date of the furthest event */
    maxDate,
    /** Date of the earliest event */
    minDate,
    /** Available dates to filter by */
    availableDates,

    /** Decrement the date filter by one month */
    previousMonth,
    /** Increment the date filter by one month */
    nextMonth,
    /** Set the from date */
    setFromDate,

    /** Available categories to filter by */
    categories,
    /** Selected category */
    category,
    /** Clear the category filter */
    clearCategory,
    /** Set the category filter */
    setCategory,

    /** Places */
    places,
    /** Selected place */
    place,
    /** Clear the place filter */
    clearPlace,
    /** Set the place filter */
    setPlace,

    /** Cities - this is a pre-reduced place filter */
    cities,
    /** Selected city */
    city,
    /** Clear the city filter - this is a pre-reduced place filter */
    clearCity,
    /** Set the city filter - this is a pre-reduced place filter */
    setCity,

    /** State of the query
     * @returns 'loading', 'idle', 'error'
     */
    status,
    /** Events */
    events,
    /** Show more events */
    showMore,
    /** Whether the last page has been reached */
    isLastPage,
  };
};
